import { useCallback, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { startOfYear, startOfDay, format } from "date-fns";
import { AccountContext } from "auth/accountProvider";
import {
  buildCpiQueryParams,
  dashboardQueryKey,
  dashboardImpl,
  emissionsByMonthQueryKey,
  emissionsByMonthImpl,
  type CpiAuth,
} from "lib/cpiQueries";
import { EMPTY_ADVANCED_FILTERS } from "pages/cpiReports/types";
import { useRegionEmissions } from "pages/cpiReports/regionMapData";

// Minimal shape of the newest upload-history row we display on the dashboard.
export interface LastUpload {
  filename: string;
  uploadedAt: string;
  overallSuccess: boolean;
}

// Resolve the session tokens the CPI/upload endpoints expect. Mirrors the
// getSessionTokens helper in CpiReports (kept local to avoid coupling).
const useCpiAuth = (): (() => Promise<CpiAuth>) => {
  const { getSession } = useContext(AccountContext);
  return useCallback(async (): Promise<CpiAuth> => {
    const session = await getSession();
    if (!session) throw new Error("Session expired. Please log in again.");
    const jwtToken = session.getAccessToken?.()?.getJwtToken?.();
    const sessionId = session.getIdToken?.()?.payload?.sessionId;
    if (!jwtToken || !sessionId) {
      throw new Error("Session tokens missing. Please log in again.");
    }
    return { jwtToken, sessionId };
  }, [getSession]);
};

/**
 * Year-to-date period: 1 Jan of the current year → today. Both dates are floored
 * to the start of the day so the value (and the derived query keys) stay stable
 * across re-mounts within a session, keeping the react-query cache warm.
 */
/** A query is "loading" for tile purposes when it has no data and no error yet. */
export const isTileLoading = (q: {
  isError: boolean;
  data: unknown;
}): boolean => !q.isError && q.data === undefined;

export const useYtdPeriod = (): { start: Date; end: Date } =>
  useMemo(() => {
    const end = startOfDay(new Date());
    return { start: startOfYear(end), end };
  }, []);

/**
 * Emissions summary + monthly series for the dashboard, over the YTD period and
 * no filters. Reuses the CpiReports query keys so the cache is shared between the
 * two pages. Auto-fetches on mount (the QueryClient's staleTime is Infinity, so
 * it runs once per session).
 */
export const useOverviewEmissions = () => {
  const getAuth = useCpiAuth();
  const { start, end } = useYtdPeriod();
  const params = useMemo(
    () => buildCpiQueryParams(start, end, [], EMPTY_ADVANCED_FILTERS),
    [start, end]
  );

  const dashboard = useQuery({
    queryKey: dashboardQueryKey(params),
    queryFn: async ({ signal }) => dashboardImpl(params, await getAuth(), signal),
  });

  const byMonth = useQuery({
    queryKey: emissionsByMonthQueryKey(params),
    queryFn: async ({ signal }) =>
      emissionsByMonthImpl(params, await getAuth(), signal),
  });

  return { dashboard, byMonth, period: { start, end } };
};

/**
 * Count of data-quality (ingestion) issue records over the YTD period — the
 * numerator used to derive the dashboard's quality-score tile. Reads only
 * `total`, which (without `groupByFile`) is the issue-row count rather than
 * the count of distinct files with issues.
 */
export const useDataQualityCount = () => {
  const getAuth = useCpiAuth();
  const { start, end } = useYtdPeriod();
  return useQuery({
    queryKey: [
      "overview",
      "dq-issues",
      format(start, "yyyy-MM-dd"),
      format(end, "yyyy-MM-dd"),
    ] as const,
    queryFn: async ({ signal }): Promise<number> => {
      const { jwtToken, sessionId } = await getAuth();
      const res = await axios.get<{ items: unknown[]; total: number }>(
        `${process.env.REACT_APP_LPI_API_URL}/quality/ingestion-issues`,
        {
          params: {
            startDate: format(start, "yyyy-MM-dd"),
            endDate: format(end, "yyyy-MM-dd"),
            page: 1,
            limit: 1,
          },
          headers: {
            Authorization: `Session ${jwtToken}`,
            "X-Session-Id": sessionId,
          },
          signal,
        }
      );
      return res.data?.total ?? 0;
    },
  });
};

/** Newest upload across all datasets (for the "Last upload" status tile). */
export const useLastUpload = () => {
  const getAuth = useCpiAuth();
  return useQuery({
    queryKey: ["overview", "last-upload"] as const,
    queryFn: async ({ signal }): Promise<LastUpload | null> => {
      const { jwtToken, sessionId } = await getAuth();
      const res = await axios.get<{ data: LastUpload[]; total: number }>(
        `${process.env.REACT_APP_LPI_API_URL}/upload/history?page=1`,
        {
          headers: {
            Authorization: `Session ${jwtToken}`,
            "X-Session-Id": sessionId,
          },
          signal,
        }
      );
      return res.data.data[0] ?? null;
    },
  });
};

/**
 * Pickup regions for the Overview's map card, over the same YTD period as every
 * other tile.
 *
 * Shares the query key with the CPI Reports map, so opening that page after the
 * dashboard reuses this response instead of refetching it.
 *
 * Requests the FINEST resolution deliberately: the thumbnail draws districts,
 * which is what the approved mock showed, and the reports page needs this grain
 * anyway to roll up locally as the user zooms. Asking for a coarser one here
 * would make the shared cache useless to it.
 */
export const useOverviewRegions = () => {
  const { start, end } = useYtdPeriod();
  return useRegionEmissions({
    startDate: format(start, "yyyy-MM-dd"),
    endDate: format(end, "yyyy-MM-dd"),
    resolution: 4,
  });
};
